import cx from 'classnames';
import _ from 'lodash';
import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { cloneChildren, useKeyPressNavigation } from '../_utils';
import styles from './menu.module.scss';
import { MenuGroup } from './menu-group';
import { MenuItem } from './menu-item';
import { MenuTheme, MenuVariant } from './types';

export interface MenuProps {
  /**
   * MenuItem as a children
   */
  children: React.ReactElement | React.ReactElement[] | React.ReactNode;

  /**
   * Unique id to identify the menu. Use to Trigger the corresponding menu
   */
  id?: string;

  /**
   * Don't handle tab navigation if already handled by component
   *
   */
  disableKeyboardNavigation?: boolean;

  /**
   * control visibility of the menu
   */
  show?: boolean;

  /**
   * flag to render as a subMenu. This is for internal Use only
   */
  isSubMenu?: boolean;

  /** class to have customize style
   * @default ''
   */
  className?: string;

  /**
   * Variant for Menu.
   * Use MenuVariant.SIDE_NAV for Side navigation component. Currently not developed for Cascading menu.
   * @default MenuVariant.DEFAULT
   */
  variant?: MenuVariant;
  /**
   * Used to handle keypress callbacks within menu item
   */
  onMenuItemKeyPressCB?: (e: React.KeyboardEvent) => void;
  /**
   * Prop to disable tab index for the outer menu element
   */
  disableTabIndex?: boolean;
  /**
   * Prop to set the theme for the menu
   */
  menuTheme?: MenuTheme;
}

type MenuType<P> = React.FC<P> & {
  Item: typeof MenuItem;
  Group: typeof MenuGroup;
};

export const Menu: MenuType<MenuProps> = ({
  id,
  children,
  className,
  isSubMenu = false,
  show = false,
  disableKeyboardNavigation = false,
  variant = MenuVariant.DEFAULT,
  onMenuItemKeyPressCB = _.noop,
  disableTabIndex = false,
  menuTheme = undefined,
  ...restProps
}: MenuProps) => {
  const menu = useRef<HTMLDivElement>(null) as MutableRefObject<HTMLDivElement>;
  const [menuStyle, setMenuStyle] = useState<string | null>();
  const isSideNavVariant = [MenuVariant.SIDE_NAV, MenuVariant.MODAL_SIDE_NAV].includes(variant);
  const actionMenuClasses = cx(styles.actionMenu, styles.withOutIcons, className, {
    [styles.sideNavVariant]: isSideNavVariant,
  });

  const setSubMenuStyle = useCallback(() => {
    const { innerWidth } = window;
    let subMenuStyle = cx(styles.cascadingMenu);
    // To calculate the current bounding box of the subMenu Element
    const rect: DOMRect = menu.current && menu.current.getBoundingClientRect();
    subMenuStyle =
      rect && rect.right + rect.width < innerWidth
        ? cx(subMenuStyle, styles.openRight)
        : cx(subMenuStyle, styles.openLeft);
    setMenuStyle(subMenuStyle);
  }, [menu]);

  useEffect(() => {
    if (isSubMenu && menu.current) {
      setSubMenuStyle();
    }
  }, [isSubMenu, menu, setSubMenuStyle, show]);

  const getSortedMenuItems = () => {
    const menuItemClass = cx(styles.item);
    const menuGroupWrapperClass = cx(styles.menuGroupWrapper);

    const menuItemList = [] as HTMLElement[];
    // identify only the menu items and ignore the inner child sub menu items
    for (let i = 0; i < menu?.current?.children?.length; i++) {
      const childElement = menu?.current?.children[i];
      // if the wrapper is there, then it means that the element is part of menu and not inside submenu
      if (childElement?.getAttribute('class')?.includes(menuGroupWrapperClass)) {
        for (let j = 0; j < childElement?.children?.length; j++) {
          const menuEle = childElement?.children[j] as HTMLElement;

          if (menuEle?.getAttribute('class')?.includes(menuItemClass)) {
            menuItemList.push(menuEle);
          }
        }
      }
    }
    const menuItemIDs: Array<string> = [];

    for (let i = 0; i < menuItemList.length; i++) {
      if (
        !_.isEmpty(menuItemList[i]?.id) &&
        !(menuItemList[i]?.id[0] <= '9' && menuItemList[i]?.id[0] >= '0') &&
        !menuItemList[i]?.id?.includes('/')
      ) {
        menuItemIDs.push(menuItemList[i]?.id);
      }
    }
    return menuItemIDs;
  };

  const menuItemIDs: string[] = getSortedMenuItems();

  const clickMenuItem = (itemID: string) => {
    if (!disableKeyboardNavigation) {
      const ele = menu?.current?.querySelector(`[id="${itemID}"]`) as HTMLOptionElement;

      ele?.click();
    }
  };

  const { cursor } = useKeyPressNavigation(document, menuItemIDs, clickMenuItem, show);

  useEffect(() => {
    if (!disableKeyboardNavigation) {
      if (cursor >= 0) {
        const ele = menu?.current?.querySelector(
          `[id="${menuItemIDs[cursor]}"]`
        ) as HTMLOptionElement;

        ele?.focus();
      }
    }
  }, [cursor, disableKeyboardNavigation, menu, menuItemIDs]);

  const renderChildren = (clsName: string) => (
    <div
      role="menuitem"
      tabIndex={disableTabIndex ? -1 : 0}
      id={id}
      ref={menu}
      className={cx(clsName, {
        [styles[menuTheme!]]: menuTheme !== undefined,
      })}
      data-testid={id}
      {...restProps}
    >
      {React.Children.toArray(children).map((item: any) => {
        if (item?.type === MenuGroup) {
          return cloneChildren(item, {
            variant,
            onMenuItemKeyPressCB,
          });
        }
        return cloneChildren(item, {
          variant,
        });
      })}
    </div>
  );

  const renderSubMenu = () => (
    <div
      className={cx(menuStyle!, {
        [styles[menuTheme!]]: menuTheme !== undefined,
      })}
      {...restProps}
      role="listbox"
      tabIndex={0}
    >
      <div className={actionMenuClasses}>{isSubMenu && renderChildren(styles.itemContainer)}</div>
    </div>
  );

  return <>{show && (isSubMenu ? renderSubMenu() : renderChildren(actionMenuClasses))}</>;
};

Menu.Item = MenuItem;
Menu.Group = MenuGroup;
